import { apiFetch } from '@/platform/lib/api';
import type { CalendarEvent } from '@/platform/calendar/types';

export type GroupVisibility = 'public' | 'private' | 'secret';
export type MemberCreateVisibility = 'public' | 'private';
export type GroupStatus = 'active' | 'read_only' | 'archived';
export type JoinPolicy = 'open' | 'approval' | 'invite_only';
export type MemberRole = 'owner' | 'admin' | 'moderator' | 'member';

export interface Group {
  id: string;
  groupId: string;
  name: string;
  description?: string;
  coverImageUrl?: string;
  avatarUrl?: string;
  ownerId: string;
  visibility: GroupVisibility;
  joinPolicy: JoinPolicy;
  postPolicy: 'members' | 'admins_only';
  status: GroupStatus;
  memberCount: number;
  claimCount: number;
  postCount: number;
  calendarId?: string;
  createdAt: string;
  /** Present on list/get when authenticated: active | pending | banned */
  viewerMembershipStatus?: 'active' | 'pending' | 'banned' | string;
}

export interface GroupMember {
  userId: string;
  role: MemberRole;
  status: 'active' | 'pending' | 'banned';
  joinedAt: string;
}

export interface LinkedClaim {
  claimId: string;
  linkedAt: string;
  linkedByUserId: string;
  ownerUserId: string;
}

export interface GroupPost {
  id: string;
  authorId: string;
  title: string;
  body: string;
  commentCount: number;
  lockedAt?: string;
  hiddenAt?: string;
  pinnedAt?: string;
  createdAt: string;
  imageObjectPaths?: string[];
  imageUrls?: string[];
  youtubeVideoId?: string;
}

export interface GroupComment {
  id: string;
  authorId: string;
  parentId?: string;
  depth: number;
  body: string;
  createdAt: string;
}

export function listGroups() {
  return apiFetch<{ groups: Group[] }>('/groups');
}

export function getGroup(groupId: string) {
  return apiFetch<{ group: Group; member?: GroupMember; claims: LinkedClaim[] }>(`/groups/${groupId}`);
}

export function createGroup(body: {
  groupId: string;
  name: string;
  description?: string;
  visibility: MemberCreateVisibility;
  joinPolicy: JoinPolicy;
}) {
  return apiFetch<Group>('/groups', { method: 'POST', body: JSON.stringify(body) });
}

export function requestGroupAccess(groupId: string) {
  return joinGroup(groupId);
}

export function joinGroup(groupId: string, inviteToken?: string) {
  return apiFetch<GroupMember>(`/groups/${groupId}/members`, {
    method: 'POST',
    body: JSON.stringify({ inviteToken }),
  });
}

export function listGroupPosts(groupId: string) {
  return apiFetch<{ posts: GroupPost[] }>(`/groups/${groupId}/posts`);
}

export function createGroupPost(
  groupId: string,
  title: string,
  body: string,
  media?: { imageObjectPaths?: string[]; youtubeUrl?: string },
) {
  return apiFetch<GroupPost>(`/groups/${groupId}/posts`, {
    method: 'POST',
    body: JSON.stringify({
      title,
      body,
      imageObjectPaths: media?.imageObjectPaths ?? [],
      youtubeUrl: media?.youtubeUrl ?? '',
    }),
  });
}

export function linkGroupClaim(groupId: string, claimId: string) {
  return apiFetch<{ status: string }>(`/groups/${groupId}/claims`, {
    method: 'POST',
    body: JSON.stringify({ claimId }),
  });
}

export function getClaimGroups(claimId: string) {
  return apiFetch<{ groups: Group[] }>(`/claims/${claimId}/groups`);
}

export function updateGroup(groupId: string, body: Partial<Pick<Group, 'name' | 'description' | 'visibility' | 'joinPolicy'>>) {
  return apiFetch<{ status: string }>(`/groups/${groupId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function deleteGroup(groupId: string) {
  return apiFetch<{ status: string }>(`/groups/${groupId}`, { method: 'DELETE' });
}

export function listGroupMembers(groupId: string) {
  return apiFetch<{ members: GroupMember[] }>(`/groups/${groupId}/members`);
}

export function updateGroupMember(groupId: string, userId: string, body: { role?: MemberRole; status?: GroupMember['status'] }) {
  return apiFetch<{ status: string }>(`/groups/${groupId}/members/${encodeURIComponent(userId)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function transferGroupOwner(groupId: string, newOwnerUserId: string) {
  return apiFetch<{ status: string }>(`/groups/${groupId}/transfer-owner`, {
    method: 'POST',
    body: JSON.stringify({ newOwnerUserId }),
  });
}

export function moderateGroupPost(groupId: string, postId: string, action: string) {
  return apiFetch<{ status: string }>(`/groups/${groupId}/posts/${postId}/moderate`, {
    method: 'POST',
    body: JSON.stringify({ action }),
  });
}

export function fetchGroupCalendarEvents(groupId: string, from: string, to: string) {
  const q = new URLSearchParams({ from, to });
  return apiFetch<{ events: CalendarEvent[] }>(
    `/groups/${groupId}/calendar/events?${q.toString()}`,
  );
}
